import { useQuery } from "@tanstack/react-query";
import {
	e8ToUsd,
	setLastCertVerifyFailure,
	verifyCertifiedAnalyticsSummaryDetailed,
	type CertifiedAnalyticsSummaryE8,
} from "@/lib/certified-analytics";
import {
	getAnonymousAgent,
	getV2ProtocolActor,
	hasProtocolEnv,
	natToAmount,
} from "@/lib/protocol-actor";
import { getIcConfig } from "@/lib/network";
import { FIXTURE } from "@/data/fixtures";

export type CertifiedStats = {
	tvlUsd: number;
	totalDebtUsd: number;
	activeVaults: number;
	liquidatableCount: number;
	atRiskCount: number;
	verified: boolean;
	source: "certified" | "live" | "fixture" | "unverified";
};

function fixtureStats(): CertifiedStats {
	return {
		tvlUsd: FIXTURE.tvlUsd,
		totalDebtUsd: FIXTURE.borrowVolumeUsd,
		activeVaults: FIXTURE.vaultCount,
		liquidatableCount: FIXTURE.liquidatable.length,
		atRiskCount: FIXTURE.vaults.filter((v) => v.health === "at_risk").length,
		verified: false,
		source: "fixture",
	};
}

function fromSummaryE8(
	s: {
		tvlE8: bigint;
		totalDebtE8: bigint;
		activeVaults: bigint;
		liquidatableCount: bigint;
		atRiskCount: bigint;
	},
	source: "certified" | "live",
): CertifiedStats {
	return {
		tvlUsd: source === "certified" ? e8ToUsd(s.tvlE8) : natToAmount(s.tvlE8),
		totalDebtUsd:
			source === "certified" ? e8ToUsd(s.totalDebtE8) : natToAmount(s.totalDebtE8),
		activeVaults: Number(s.activeVaults),
		liquidatableCount: Number(s.liquidatableCount),
		atRiskCount: Number(s.atRiskCount),
		verified: source === "certified",
		source,
	};
}

async function liveQuery(
	actor: NonNullable<Awaited<ReturnType<typeof getV2ProtocolActor>>>,
): Promise<CertifiedStats | null> {
	try {
		const s = await actor.getAnalyticsSummaryQuery();
		return fromSummaryE8(s, "live");
	} catch {
		return null;
	}
}

/**
 * Dashboard metrics: prefer live protocol query, then upgrade badge if IC cert verifies.
 * "Verified" = cryptographic CertifiedData digest — not admin approval.
 */
export function useCertifiedAnalytics() {
	return useQuery({
		queryKey: ["v2", "certifiedAnalytics"],
		queryFn: async (): Promise<CertifiedStats> => {
			if (!hasProtocolEnv()) {
				setLastCertVerifyFailure(null);
				return fixtureStats();
			}

			const actor = await getV2ProtocolActor();
			const agent = await getAnonymousAgent();
			const config = getIcConfig();
			if (!actor || !agent || !config.protocolCanisterId) {
				setLastCertVerifyFailure(null);
				return fixtureStats();
			}

			const live = await liveQuery(actor);

			try {
				const { certificate, summary } =
					await actor.getCertifiedAnalyticsSummary();
				const s = summary as CertifiedAnalyticsSummaryE8;
				const result = await verifyCertifiedAnalyticsSummaryDetailed(
					agent,
					config.protocolCanisterId,
					new Uint8Array(certificate as Uint8Array),
					s,
				);

				if (result.ok) {
					setLastCertVerifyFailure(null);
					return fromSummaryE8(s, "certified");
				}
				setLastCertVerifyFailure(result.reason);
			} catch {
				setLastCertVerifyFailure("exception");
			}

			if (live) return live;

			return {
				tvlUsd: 0,
				totalDebtUsd: 0,
				activeVaults: 0,
				liquidatableCount: 0,
				atRiskCount: 0,
				verified: false,
				source: "unverified",
			};
		},
		staleTime: 60_000,
		retry: 1,
	});
}
